/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import Picture from 'components/Picture/Picture';
import VideoBackground from 'components/VideoBackground/VideoBackground';
import Text from './Text/Text';
import './Story.scss';

/**
 * This is the Story component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Story({ pages }) {
  const [current, setCurrent] = useState(0);

  const images = pages.map(({ image }) => image);
  const videos = pages.map(({ video }) => video ?? null);

  const texts = pages.reduce((accumulator, currentValue, index) => {
    const texts = currentValue.texts.map(text => ({ image: index, text }));
    return [...accumulator, ...texts];
  }, []);

  return (
    <div className={classnames('story')} appear="true">
      <div className="images">
        {images.map((props, index) => (
          <CSSTransition
            key={index}
            in={index === current}
            timeout={1000}
            appear
          >
            <div className="media">
              <Picture {...props} />
              {videos[index] ? <VideoBackground {...videos[index]} /> : null}
            </div>
          </CSSTransition>
        ))}
      </div>

      <div className="texts">
        {texts.map(({ image, ...props }, index) => (
          <Text
            key={index}
            {...props}
            onInview={() => {
              setCurrent(image);
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Story.propTypes = {
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      image: PropTypes.object.isRequired,
      video: PropTypes.object,
      texts: PropTypes.array.isRequired,
    })
  ).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Story.defaultProps = {
  pages: [],
};

export default Story;
